'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, ShieldCheck, Copy, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface DemoCredential {
  role: string;
  email: string;
  password: string;
  color: string;
}

const demoCredentials: DemoCredential[] = [
  { role: 'AP Clerk', email: 'ap.clerk@meridianholdings.com', password: 'LedgerAP#2026', color: 'text-primary' },
  { role: 'CFO', email: 'cfo@meridianholdings.com', password: 'LedgerCFO#2026', color: 'text-success' },
];

const isDev = process.env.NODE_ENV === 'development';

export default function LoginForm() {
  const router = useRouter();
  const { signIn, profile } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>();

  const handleCopy = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const autofill = (cred: DemoCredential) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
  };

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      const userRole = result?.user?.user_metadata?.role || 'ap_clerk';
      toast.success('Welcome back! Signed in successfully.', {
        description: 'Redirecting to your dashboard...',
      });
      // Route based on role
      if (userRole === 'cfo' || userRole === 'controller' || userRole === 'owner') {
        router.push('/cfo-dashboard');
      } else {
        router.push('/');
      }
      router.refresh();
    } catch (err: any) {
      setLoading(false);
      setError('password', {
        type: 'manual',
        message: err?.message || 'Invalid credentials. Please check your email and password.',
      });
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">Sign in to LedgerPay</h2>
        <p className="text-sm text-muted-foreground mt-1">Access your payment authorization workspace</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Email */}
        <div>
          <label htmlFor="login-email" className="block text-sm font-semibold text-foreground mb-1.5">
            Work Email
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            {...register('email', {
              required: 'Email address is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
            className={`w-full px-3.5 py-2.5 text-sm border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors duration-150 ${errors.email ? 'border-danger focus:ring-danger/30' : 'border-input'}`}
          />
          {errors.email && <p className="text-xs text-danger mt-1" role="alert">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="text-sm font-semibold text-foreground">
              Password
            </label>
            <Link href="/forgot-password" className="text-xs text-primary hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••••••"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
              className={`w-full pr-10 px-3.5 py-2.5 text-sm border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors duration-150 ${errors.password ? 'border-danger focus:ring-danger/30' : 'border-input'}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-danger mt-1" role="alert">{errors.password.message}</p>}
        </div>

        {/* Remember Me */}
        <div className="flex items-center gap-2">
          <input
            id="remember-me"
            type="checkbox"
            {...register('rememberMe')}
            className="w-4 h-4 rounded border-input accent-primary"
          />
          <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer">
            Keep me signed in for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-bold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 active:scale-[0.98] transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Verifying credentials...
            </>
          ) : (
            <>
              <ShieldCheck size={16} />
              Sign In Securely
            </>
          )}
        </button>
      </form>

      {/* Demo Credentials — development only */}
      {isDev && (
      <div className="mt-6 border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 bg-muted/50 border-b border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Demo Accounts — Click to autofill</p>
        </div>
        <div className="divide-y divide-border">
          {demoCredentials.map((cred, i) => (
            <div key={`demo-cred-${i}`} className="px-4 py-3 hover:bg-muted/30 transition-colors duration-150">
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-xs font-bold uppercase tracking-wider ${cred.color}`}>{cred.role}</span>
                <button
                  onClick={() => autofill(cred)}
                  className="text-xs text-primary font-semibold hover:underline px-2 py-0.5 rounded hover:bg-primary/5 transition-colors duration-150"
                >
                  Use this account
                </button>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground font-tabular truncate flex-1">{cred.email}</span>
                  <button
                    onClick={() => handleCopy(cred.email, `email-${i}`)}
                    className="flex-shrink-0 p-1 rounded hover:bg-muted transition-colors duration-150 text-muted-foreground hover:text-foreground"
                    aria-label="Copy email"
                  >
                    {copiedField === `email-${i}` ? <CheckCheck size={12} className="text-success" /> : <Copy size={12} />}
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground font-tabular">••••••••••••</span>
                  <button
                    onClick={() => handleCopy(cred.password, `pass-${i}`)}
                    className="flex-shrink-0 p-1 rounded hover:bg-muted transition-colors duration-150 text-muted-foreground hover:text-foreground"
                    aria-label="Copy password"
                  >
                    {copiedField === `pass-${i}` ? <CheckCheck size={12} className="text-success" /> : <Copy size={12} />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}